import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { InfoTooltip } from "components/ui/InfoTooltip";
import { Select } from "components/ui/Select/Select";
import { isPregnancyConditionVisible } from "features/cdm/pp/utils/visibleRegistrationReasons";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { ItemFormWrapper } from "../../../../../components/ItemFormWrapper/ItemFormWrapper";
import style from "./OutcomeForm.module.css";

const PRE_DIABETES_CRITERIA =
  "Pre diabetes can be diagnosed when HBA1c lies between 42 – 47 mmol / mol";

const options = [
  {
    label:
      "Normal – does not currently meet criteria – review again after 5 years",
    value: "NORMAL",
  },
  {
    label: "Diagnosed with a chronic disease",
    value: "DIAGNOSED_WITH_CHRONIC_DISEASE",
  },
  {
    label: "Prevention Programme",
    value: "REGISTER_ON_PREVENTION_PROGRAMME",
  },
];

const formSchema = z.object({
  outcome: z.string().min(1, "Outcome is required"),

  qrisk3: z.boolean(),
  hypertenstionStage1: z.boolean(),
  hypertenstionStage1NoTargetOrganDamage: z.boolean(),
  hypertenstionStage2or3: z.boolean(),
  gestationalDiabetes: z.boolean(),
  preEclampsia: z.boolean(),
  preDiabetes: z.boolean(),
  previousBNP: z.boolean(),

  diabetesType2: z.boolean(),
  asthma: z.boolean(),
  copd: z.boolean(),
  heartFailure: z.boolean(),
  atrialFibrillation: z.boolean(),
  ischaemicHeartDisease: z.boolean(),
  stroke: z.boolean(),
  tia: z.boolean(),
});

export const OutcomeForm = ({
  onBack,
  onContinue,
  data,
  isReview = false,
  isLoading,
}) => {
  const { data: patient } = usePatientData();

  // Pregnancy reasons are gated to female patients over 18 (GP Contract v3-2).
  // Keep a checkbox visible if it was already ticked on a saved review so an
  // existing value is never silently dropped on edit/preview.
  const showGestationalDiabetes =
    isPregnancyConditionVisible(patient) || !!data?.gestationalDiabetes;
  const showPreEclampsia =
    isPregnancyConditionVisible(patient) || !!data?.preEclampsia;

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      outcome: data?.outcome || "NORMAL",

      qrisk3: data?.qrisk3 || false,
      hypertenstionStage1: data?.hypertenstionStage1 || false,
      hypertenstionStage1NoTargetOrganDamage:
        data?.hypertenstionStage1NoTargetOrganDamage || false,
      hypertenstionStage2or3: data?.hypertenstionStage2or3 || false,
      gestationalDiabetes: data?.gestationalDiabetes || false,
      preEclampsia: data?.preEclampsia || false,
      preDiabetes: data?.preDiabetes || false,
      previousBNP: data?.previousBNP || false,

      diabetesType2: data?.diabetesType2 || false,
      asthma: data?.asthma || false,
      copd: data?.copd || false,
      heartFailure: data?.heartFailure || false,
      atrialFibrillation: data?.atrialFibrillation || false,
      ischaemicHeartDisease: data?.ischaemicHeartDisease || false,
      stroke: data?.stroke || false,
      tia: data?.tia || false,
    },
  });

  useEffect(() => {
    if (data?.outcome) {
      form.setValue("outcome", data.outcome);
    }
  }, [data.outcome, form]);

  const handleFormSubmit = (values) => {
    onContinue(values);
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("OutcomeForm"))}
      isLoading={isLoading}
      isLastStep={true}
      isReview={isReview}
    >
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          paddingBottom: "0px",
          borderBottom: "none",
        }}
      >
        <h3>Opportunistic Case Finding Outcome</h3>
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("OutcomeForm"))}
          >
            {data?.ocfSuggestedOutcome &&
              data?.ocfSuggestedOutcomeReason &&
              options.find(
                (option) => option.value === data?.ocfSuggestedOutcome
              ) && (
                <div>
                  <h3 style={{ marginBottom: "12px" }}>
                    Suggested Outcome:{" "}
                    {
                      options.find(
                        (option) => option.value === data?.ocfSuggestedOutcome
                      ).label
                    }
                  </h3>
                  <p>{data?.ocfSuggestedOutcomeReason}</p>
                </div>
              )}
            <div
              className={style.inputGroupGrid2Columns}
              style={{ marginBottom: "12px" }}
            >
              <FormField name="outcome">
                <label className={style.addReferralFormLabel}>Outcome</label>
                <Select
                  options={options}
                  value={form.watch("outcome")}
                  onChange={(value) =>
                    form.setValue("outcome", value, { shouldValidate: true })
                  }
                  required
                  disabled={isReview}
                  readOnly={isReview}
                />
              </FormField>
            </div>
            {form.watch("outcome") === "REGISTER_ON_PREVENTION_PROGRAMME" && (
              <fieldset className={style.medsCheckboxGroup}>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("qrisk3")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  {"QRISK 3>=20%"}
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("hypertenstionStage1")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  Hypertension Stage 1 (BP 140/90 to 159/99mmHg) with target
                  organ damage
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("hypertenstionStage1NoTargetOrganDamage")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  Hypertension Stage 1 (BP 140/90 to 155/99mmHg) with NO target
                  organ damage
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("hypertenstionStage2or3")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  {"Hypertension Stage 2 or 3 (BP>160/100mmHg)"}
                </label>
                {showGestationalDiabetes && (
                  <label className={style.pregnancyFormCheckboxLabel}>
                    <CustomCheckbox
                      {...form.register("gestationalDiabetes")}
                      disabled={isReview}
                      readOnly={isReview}
                    />
                    Gestational Diabetes (if diagnosed since 1st January, 2023)
                    All cardholders &amp; Non GMS
                  </label>
                )}
                {showPreEclampsia && (
                  <label className={style.pregnancyFormCheckboxLabel}>
                    <CustomCheckbox
                      {...form.register("preEclampsia")}
                      disabled={isReview}
                      readOnly={isReview}
                    />
                    Pre-eclampsia (if diagnosed since 1st January, 2023) All
                    cardholders &amp; Non GMS
                  </label>
                )}
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("preDiabetes")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  Pre-Diabetes
                  <InfoTooltip text={PRE_DIABETES_CRITERIA} />
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("previousBNP")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  {
                    "Previous BNP greater or equal to 34 pg/ml or NT pro BNP greater or equal to 125 pg/ml, if previously recorded"
                  }
                </label>
              </fieldset>
            )}
            {form.watch("outcome") === "DIAGNOSED_WITH_CHRONIC_DISEASE" && (
              <fieldset className={style.medsCheckboxGroup}>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("diabetesType2")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  Diabetes Type 2
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("asthma")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  Asthma
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("copd")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  COPD
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("heartFailure")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  Heart Failure
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("atrialFibrillation")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  Atrial Fibrillation
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("ischaemicHeartDisease")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  Ischaemic Heart Disease
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("stroke")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  Cerebrovascular Disease Stroke
                </label>
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    {...form.register("tia")}
                    disabled={isReview}
                    readOnly={isReview}
                  />
                  Cerebrovascular Disease TIA
                </label>
              </fieldset>
            )}
          </form>
        </FormProvider>
      </div>
    </ItemFormWrapper>
  );
};
